import type { EntitlementKey } from '../../api/entitlements';

// ---------------------------------------------------------------------------
// Insights (web app v4 plan §5.1): Reports and Reflections as one page at
// /insights/:tab, five tabs whose ids are the URL segment. This file is the
// one place that knows the tabs, who may open each, and how an old /reports
// address maps onto them, so the page, the redirect table and the tests read
// the same rules.
// ---------------------------------------------------------------------------

export type InsightsTabId = 'summary' | 'time' | 'drivers' | 'activity' | 'reflections';

export interface InsightsTab {
  id: InsightsTabId;
  label: string;
}

export const INSIGHTS_TABS: readonly InsightsTab[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'time', label: 'Time' },
  { id: 'drivers', label: 'Drivers & projects' },
  { id: 'activity', label: 'Activity' },
  { id: 'reflections', label: 'Reflections' },
];

/** The tabs that are reports, and so need the canViewReports permission. Reflections is open to everyone. */
export const REPORT_TAB_IDS: readonly InsightsTabId[] = ['summary', 'time', 'drivers', 'activity'];

export const INSIGHTS_BASE = '/insights';

/** The "All executives" side of the Me / All executives switch, as a query value (`?view=all`). */
export const ALL_EXECUTIVES_VIEW = 'all';

/**
 * What a person may see on Insights. The same checks Reports made on its
 * eight tabs (ReportsPage L199-230 before the move), now folded into five:
 *   - Summary (was My Reports), Time (Time Allocation) and Activity: basic
 *     reporting, on every plan.
 *   - Drivers & projects: Strategic Drivers needed advanced reporting and
 *     strategic drivers; Projects needed projects. The tab shows when either
 *     half does, and only that half.
 *   - Activity's Delegation half needs delegation and advanced reporting.
 *   - The Me / All executives switch (was the Cross-Executive tab) needs the
 *     cross-executive entitlement and an Admin, TenantAdmin or EA role.
 *   - Every report needs canViewReports; Reflections is everyone's.
 * The Capacity tab isn't here: capacity lives under Focus now.
 */
export interface InsightsAccessFacts {
  canViewReports: boolean;
  /** Show-by-default entitlement check (EntitlementsContext.has). */
  has: (entitlement: EntitlementKey) => boolean;
  role: string | undefined;
}

export interface InsightsAccess {
  /** The tabs to show, in order. */
  tabs: InsightsTabId[];
  /** Drivers & projects: the strategic drivers half (KPIs, progress). */
  driversReport: boolean;
  /** Drivers & projects: the projects half (portfolio, stale projects). */
  projectsReport: boolean;
  /** Activity: the delegation half. */
  delegationReport: boolean;
  /** Summary: the Me / All executives switch. */
  allExecutives: boolean;
}

export function insightsAccess({ canViewReports, has, role }: InsightsAccessFacts): InsightsAccess {
  const advancedReporting = has('advancedReporting');
  const driversReport = canViewReports && advancedReporting && has('strategicDrivers');
  const projectsReport = canViewReports && has('projects');
  const delegationReport = canViewReports && has('delegation') && advancedReporting;
  const crossExecRole = role === 'Admin' || role === 'TenantAdmin' || role === 'ExecutiveAssistant';
  const allExecutives = canViewReports && has('crossExecutiveReporting') && crossExecRole;

  const tabs: InsightsTabId[] = [];
  if (canViewReports) tabs.push('summary', 'time');
  if (driversReport || projectsReport) tabs.push('drivers');
  if (canViewReports) tabs.push('activity');
  tabs.push('reflections');

  return { tabs, driversReport, projectsReport, delegationReport, allExecutives };
}

/** Where Insights opens when no tab is named: Summary for people who may see reports, else Reflections. */
export function defaultInsightsTab(canViewReports: boolean): InsightsTabId {
  return canViewReports ? 'summary' : 'reflections';
}

export function insightsTabPath(tab: InsightsTabId): string {
  return `${INSIGHTS_BASE}/${tab}`;
}

// Today's /reports never puts its tab in the address (the tab was component
// state), and nothing links to it with one: the weekly email links to bare
// /reports, the Mac to /reflections. These rows cover a ?tab= anyway, by the
// old tab number (0-7, the order ReportsPage listed them) or by name, so an
// address someone made by hand still lands somewhere sensible.
const OLD_REPORTS_TABS: Readonly<Record<string, { path: string; query?: Record<string, string> }>> = {
  '0': { path: insightsTabPath('summary') },
  'my-reports': { path: insightsTabPath('summary') },
  summary: { path: insightsTabPath('summary') },
  // Capacity lives under Focus now.
  '1': { path: '/focus/capacity' },
  capacity: { path: '/focus/capacity' },
  '2': { path: insightsTabPath('drivers') },
  drivers: { path: insightsTabPath('drivers') },
  'strategic-drivers': { path: insightsTabPath('drivers') },
  '3': { path: insightsTabPath('drivers') },
  projects: { path: insightsTabPath('drivers') },
  '4': { path: insightsTabPath('time') },
  time: { path: insightsTabPath('time') },
  'time-allocation': { path: insightsTabPath('time') },
  '5': { path: insightsTabPath('activity') },
  activity: { path: insightsTabPath('activity') },
  '6': { path: insightsTabPath('activity') },
  delegation: { path: insightsTabPath('activity') },
  '7': { path: insightsTabPath('summary'), query: { view: ALL_EXECUTIVES_VIEW } },
  'cross-executive': { path: insightsTabPath('summary'), query: { view: ALL_EXECUTIVES_VIEW } },
  reflections: { path: insightsTabPath('reflections') },
};

/**
 * Where an old /reports address goes, from its query string (with or without
 * the leading "?"). `tab` picks the page and is dropped; every other key is
 * kept, after any the target sets itself. No tab, or one it doesn't know,
 * gives bare /insights, which opens Summary or Reflections by permission.
 *
 *   insightsPathFor('')                  → '/insights'
 *   insightsPathFor('?tab=4&from=x')     → '/insights/time?from=x'
 *   insightsPathFor('?tab=7')            → '/insights/summary?view=all'
 *   insightsPathFor('?tab=1')            → '/focus/capacity'
 *
 * The result already carries the query, so a redirect using it shouldn't
 * merge the old query in again (that would put `tab` back).
 */
export function insightsPathFor(oldReportsSearch: string): string {
  const params = new URLSearchParams(oldReportsSearch);
  const tab = (params.get('tab') ?? '').trim().toLowerCase();
  params.delete('tab');

  const target = OLD_REPORTS_TABS[tab] ?? { path: INSIGHTS_BASE };
  const query = new URLSearchParams(target.query);
  params.forEach((value, key) => {
    if (!query.has(key)) query.append(key, value);
  });
  const search = query.toString();
  return search ? `${target.path}?${search}` : target.path;
}
